// Mesures de cadence indépendantes du modem optique.
//
// Trois informations volontairement distinctes :
// - getCapabilities() : ce que la piste annonce pouvoir accepter ;
// - getSettings() : le réglage que le navigateur annonce avoir appliqué ;
// - rVFC/mediaTime : les images vidéo réellement présentées au document.

export const DEFAULT_STABILITY_THRESHOLDS = Object.freeze({
  maxMadRatio: 0.12,
  maxP10P90SpreadRatio: 0.30,
  maxLossRate: 0.05,
});

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isPositiveNumber = (value) => isFiniteNumber(value) && value > 0;

function errorMessage(error) {
  if (error instanceof Error && error.message) return error.message;
  return String(error || 'unknown error');
}

function optionalFunction(options, key, fallback) {
  const value = hasOwn(options, key) ? options[key] : fallback;
  return typeof value === 'function' ? value : null;
}

function defaultNow() {
  return globalThis.performance?.now?.() ?? Date.now();
}

function normalizeFrameRateCapability(value) {
  if (isPositiveNumber(value)) {
    const frameRate = Number(value);
    return { min: frameRate, max: frameRate, step: null };
  }

  if (!value || typeof value !== 'object') return null;

  const min = isPositiveNumber(value.min) ? Number(value.min) : null;
  const max = isPositiveNumber(value.max) ? Number(value.max) : null;
  const step = isPositiveNumber(value.step) ? Number(value.step) : null;
  if (min === null && max === null) return null;

  return { min, max, step };
}

/**
 * Lit les déclarations d'une MediaStreamTrack sans supposer que Safari expose
 * getCapabilities(), ni qu'un pilote accepte réellement le FPS demandé.
 */
export function inspectVideoTrack(track) {
  const capabilities = {
    supported: typeof track?.getCapabilities === 'function',
    frameRate: null,
    error: null,
  };
  const settings = {
    supported: typeof track?.getSettings === 'function',
    frameRate: null,
    width: null,
    height: null,
    error: null,
  };

  if (capabilities.supported) {
    try {
      const value = track.getCapabilities();
      capabilities.frameRate = normalizeFrameRateCapability(value?.frameRate);
    } catch (error) {
      capabilities.error = errorMessage(error);
    }
  }

  if (settings.supported) {
    try {
      const value = track.getSettings();
      settings.frameRate = isPositiveNumber(value?.frameRate)
        ? Number(value.frameRate)
        : null;
      settings.width = isPositiveNumber(value?.width) ? Number(value.width) : null;
      settings.height = isPositiveNumber(value?.height) ? Number(value.height) : null;
    } catch (error) {
      settings.error = errorMessage(error);
    }
  }

  return { capabilities, settings };
}

function quantile(sortedValues, ratio) {
  if (sortedValues.length === 0) return null;
  if (sortedValues.length === 1) return sortedValues[0];

  const position = (sortedValues.length - 1) * ratio;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  const weight = position - lowerIndex;
  return sortedValues[lowerIndex]
    + ((sortedValues[upperIndex] - sortedValues[lowerIndex]) * weight);
}

function intervalStatistics(intervals) {
  if (intervals.length === 0) {
    return {
      min: null,
      max: null,
      mean: null,
      p10: null,
      median: null,
      p90: null,
      mad: null,
    };
  }

  const sorted = [...intervals].sort((a, b) => a - b);
  const median = quantile(sorted, 0.5);
  const deviations = intervals
    .map((value) => Math.abs(value - median))
    .sort((a, b) => a - b);

  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    mean: intervals.reduce((sum, value) => sum + value, 0) / intervals.length,
    p10: quantile(sorted, 0.1),
    median,
    p90: quantile(sorted, 0.9),
    mad: quantile(deviations, 0.5),
  };
}

function lossFromFrameNumbers(frameNumbers, sampleCount) {
  let usableDeltas = 0;
  let lostFrames = 0;

  for (let index = 1; index < frameNumbers.length; index += 1) {
    const previous = frameNumbers[index - 1];
    const current = frameNumbers[index];
    if (!Number.isInteger(previous) || !Number.isInteger(current) || current <= previous) {
      continue;
    }
    usableDeltas += 1;
    lostFrames += Math.max(0, current - previous - 1);
  }

  if (usableDeltas === 0) return null;
  return {
    count: lostFrames,
    rate: lostFrames / (sampleCount + lostFrames),
    source: 'presentedFrames',
    estimated: false,
  };
}

function estimatedLoss(intervals, expectedIntervalMs, sampleCount, source) {
  if (!isPositiveNumber(expectedIntervalMs) || intervals.length === 0) return null;

  const lostFrames = intervals.reduce((sum, interval) => {
    const slots = Math.max(1, Math.round(interval / expectedIntervalMs));
    return sum + Math.max(0, slots - 1);
  }, 0);

  return {
    count: lostFrames,
    rate: lostFrames / (sampleCount + lostFrames),
    source,
    estimated: true,
  };
}

function normalizeThresholds(overrides = {}) {
  const thresholds = { ...DEFAULT_STABILITY_THRESHOLDS };
  for (const key of Object.keys(thresholds)) {
    if (isFiniteNumber(overrides[key]) && Number(overrides[key]) >= 0) {
      thresholds[key] = Number(overrides[key]);
    }
  }
  return thresholds;
}

/**
 * Résume une série de timestamps monotones exprimés en millisecondes.
 * frameNumbers permet d'utiliser metadata.presentedFrames comme compteur de
 * pertes. À défaut, les pertes sont explicitement signalées comme estimées.
 */
export function summarizeTimestamps(timestampsMs, options = {}) {
  const sourceValues = Array.isArray(timestampsMs) ? timestampsMs : [];
  const sourceFrameNumbers = Array.isArray(options.frameNumbers)
    ? options.frameNumbers
    : [];
  const monotonicTimes = [];
  const alignedFrameNumbers = [];
  let invalidSamples = 0;
  let nonIncreasingSamples = 0;

  for (let index = 0; index < sourceValues.length; index += 1) {
    const value = Number(sourceValues[index]);
    if (!Number.isFinite(value)) {
      invalidSamples += 1;
      continue;
    }
    const previousTime = monotonicTimes[monotonicTimes.length - 1];
    if (monotonicTimes.length > 0 && value <= previousTime) {
      nonIncreasingSamples += 1;
      continue;
    }
    monotonicTimes.push(value);
    const frameNumber = sourceFrameNumbers[index];
    alignedFrameNumbers.push(Number.isInteger(frameNumber) ? frameNumber : null);
  }

  const intervals = [];
  for (let index = 1; index < monotonicTimes.length; index += 1) {
    intervals.push(monotonicTimes[index] - monotonicTimes[index - 1]);
  }

  const intervalMs = intervalStatistics(intervals);
  const elapsedMs = monotonicTimes.length >= 2
    ? monotonicTimes[monotonicTimes.length - 1] - monotonicTimes[0]
    : 0;
  const deliveredFps = elapsedMs > 0
    ? ((monotonicTimes.length - 1) * 1000) / elapsedMs
    : null;

  let loss = lossFromFrameNumbers(alignedFrameNumbers, monotonicTimes.length);
  const expectedFps = isPositiveNumber(options.expectedFps)
    ? Number(options.expectedFps)
    : null;

  if (!loss && expectedFps !== null) {
    loss = estimatedLoss(
      intervals,
      1000 / expectedFps,
      monotonicTimes.length,
      'expectedFps',
    );
  }
  if (!loss && options.inferLosses === true && isPositiveNumber(intervalMs.median)) {
    loss = estimatedLoss(
      intervals,
      intervalMs.median,
      monotonicTimes.length,
      'inferredMedian',
    );
  }
  if (!loss) {
    loss = {
      count: null,
      rate: null,
      source: 'unavailable',
      estimated: null,
    };
  }

  const thresholds = normalizeThresholds(options.stabilityThresholds);
  const madRatio = isPositiveNumber(intervalMs.median)
    ? intervalMs.mad / intervalMs.median
    : null;
  const p10P90SpreadMs = intervalMs.p10 === null || intervalMs.p90 === null
    ? null
    : intervalMs.p90 - intervalMs.p10;
  const p10P90SpreadRatio = isPositiveNumber(intervalMs.median)
    ? p10P90SpreadMs / intervalMs.median
    : null;
  const sufficient = intervals.length >= 2;
  const lossWithinThreshold = loss.rate === null || loss.rate <= thresholds.maxLossRate;
  const stable = sufficient
    && madRatio <= thresholds.maxMadRatio
    && p10P90SpreadRatio <= thresholds.maxP10P90SpreadRatio
    && lossWithinThreshold;

  return {
    sampleCount: monotonicTimes.length,
    intervalCount: intervals.length,
    elapsedMs,
    deliveredFps,
    intervalMs,
    loss,
    stability: {
      stable,
      sufficient,
      madRatio,
      p10P90SpreadMs,
      p10P90SpreadRatio,
      thresholds,
    },
    rejectedSamples: {
      invalid: invalidSamples,
      nonIncreasing: nonIncreasingSamples,
    },
  };
}

function abortError() {
  if (typeof DOMException === 'function') {
    return new DOMException('Measurement aborted', 'AbortError');
  }
  const error = new Error('Measurement aborted');
  error.name = 'AbortError';
  return error;
}

function normalizeCollectionOptions(options) {
  const durationMs = options.durationMs === Infinity
    ? Infinity
    : Number(options.durationMs ?? 2000);
  const maxFrames = options.maxFrames === Infinity
    ? Infinity
    : Number(options.maxFrames ?? 120);

  if (durationMs !== Infinity && (!Number.isFinite(durationMs) || durationMs <= 0)) {
    throw new TypeError('durationMs must be positive or Infinity');
  }
  if (maxFrames !== Infinity && (!Number.isInteger(maxFrames) || maxFrames < 2)) {
    throw new TypeError('maxFrames must be an integer >= 2 or Infinity');
  }
  if (durationMs === Infinity && maxFrames === Infinity) {
    throw new TypeError('durationMs and maxFrames cannot both be Infinity');
  }

  return { durationMs, maxFrames };
}

function collectScheduledSamples({
  request,
  cancel,
  onCallback,
  durationMs,
  maxFrames,
  now,
  setTimeoutFn,
  clearTimeoutFn,
  signal,
}) {
  return new Promise((resolve, reject) => {
    const samples = [];
    const startedAt = now();
    let callbackCount = 0;
    let requestId = null;
    let timeoutId = null;
    let settled = false;

    const cleanup = () => {
      if (requestId !== null && cancel) {
        try { cancel(requestId); } catch { /* best effort */ }
      }
      requestId = null;
      if (timeoutId !== null && clearTimeoutFn) clearTimeoutFn(timeoutId);
      timeoutId = null;
      signal?.removeEventListener?.('abort', onAbort);
    };

    const finish = (stoppedBy) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ samples, callbackCount, stoppedBy });
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const onAbort = () => fail(abortError());

    const scheduleNext = () => {
      if (settled) return;
      try {
        const id = request(onScheduledCallback);
        if (settled) {
          if (cancel) cancel(id);
        } else {
          requestId = id;
        }
      } catch (error) {
        fail(error);
      }
    };

    const onScheduledCallback = (...args) => {
      if (settled) return;
      requestId = null;
      callbackCount += 1;

      try {
        const sample = onCallback(...args);
        if (sample !== null && sample !== undefined) samples.push(sample);
      } catch (error) {
        fail(error);
        return;
      }

      if (samples.length >= maxFrames) {
        finish('maxFrames');
        return;
      }
      if (durationMs !== Infinity && now() - startedAt >= durationMs) {
        finish('duration');
        return;
      }
      scheduleNext();
    };

    if (signal?.aborted) {
      fail(abortError());
      return;
    }
    signal?.addEventListener?.('abort', onAbort, { once: true });

    if (durationMs !== Infinity) {
      if (!setTimeoutFn) {
        fail(new Error('setTimeout is unavailable'));
        return;
      }
      timeoutId = setTimeoutFn(() => finish('duration'), durationMs);
    }

    scheduleNext();
  });
}

function resolveTrack(video, options) {
  if (hasOwn(options, 'track')) return options.track;
  try {
    return video?.srcObject?.getVideoTracks?.()[0] ?? null;
  } catch {
    return null;
  }
}

function schedulingDependencies(options) {
  return {
    now: optionalFunction(options, 'now', defaultNow),
    setTimeoutFn: optionalFunction(options, 'setTimeoutFn', globalThis.setTimeout?.bind(globalThis)),
    clearTimeoutFn: optionalFunction(options, 'clearTimeoutFn', globalThis.clearTimeout?.bind(globalThis)),
  };
}

/**
 * Mesure le FPS vidéo délivré. rVFC/mediaTime est prioritaire ; sur Safari
 * sans rVFC, rAF sonde currentTime et élimine les rafraîchissements écran qui
 * ne correspondent pas à une nouvelle image vidéo.
 */
export async function measureVideoFrameRate(video, options = {}) {
  if (!video || typeof video !== 'object') throw new TypeError('video is required');

  const { durationMs, maxFrames } = normalizeCollectionOptions(options);
  const dependencies = schedulingDependencies(options);
  if (!dependencies.now) throw new Error('A monotonic clock is unavailable');

  const track = resolveTrack(video, options);
  const declared = inspectVideoTrack(track);
  const configuredFps = isPositiveNumber(options.expectedFps)
    ? Number(options.expectedFps)
    : declared.settings.frameRate;

  const nativeRvfc = typeof video.requestVideoFrameCallback === 'function'
    ? video.requestVideoFrameCallback.bind(video)
    : null;
  const nativeCancelRvfc = typeof video.cancelVideoFrameCallback === 'function'
    ? video.cancelVideoFrameCallback.bind(video)
    : null;
  const requestRvfc = optionalFunction(options, 'requestVideoFrameCallback', nativeRvfc);
  const cancelRvfc = optionalFunction(options, 'cancelVideoFrameCallback', nativeCancelRvfc);

  if (requestRvfc) {
    const collection = await collectScheduledSamples({
      request: requestRvfc,
      cancel: cancelRvfc,
      onCallback: (callbackTime, metadata = {}) => ({
        callbackTimeMs: isFiniteNumber(callbackTime)
          ? Number(callbackTime)
          : dependencies.now(),
        mediaTimeMs: isFiniteNumber(metadata.mediaTime)
          ? Number(metadata.mediaTime) * 1000
          : null,
        presentedFrames: Number.isInteger(metadata.presentedFrames)
          ? metadata.presentedFrames
          : null,
      }),
      durationMs,
      maxFrames,
      ...dependencies,
      signal: options.signal,
    });

    const mediaSamples = collection.samples.filter((sample) => sample.mediaTimeMs !== null);
    const useMediaTime = mediaSamples.length >= 2;
    const samples = useMediaTime ? mediaSamples : collection.samples;
    const timestamps = samples.map((sample) => (
      useMediaTime ? sample.mediaTimeMs : sample.callbackTimeMs
    ));
    const frameNumbers = samples.map((sample) => sample.presentedFrames);
    const summary = summarizeTimestamps(timestamps, {
      frameNumbers,
      expectedFps: configuredFps,
      inferLosses: true,
      stabilityThresholds: options.stabilityThresholds,
    });

    return {
      source: 'requestVideoFrameCallback',
      timeline: useMediaTime ? 'mediaTime' : 'callbackTime',
      declared,
      ...summary,
      callbacks: {
        total: collection.callbackCount,
        accepted: collection.samples.length,
        withoutMediaTime: collection.samples.length - mediaSamples.length,
      },
      stoppedBy: collection.stoppedBy,
    };
  }

  const nativeRaf = typeof globalThis.requestAnimationFrame === 'function'
    ? globalThis.requestAnimationFrame.bind(globalThis)
    : null;
  const nativeCancelRaf = typeof globalThis.cancelAnimationFrame === 'function'
    ? globalThis.cancelAnimationFrame.bind(globalThis)
    : null;
  const requestRaf = optionalFunction(options, 'requestAnimationFrame', nativeRaf);
  const cancelRaf = optionalFunction(options, 'cancelAnimationFrame', nativeCancelRaf);
  if (!requestRaf) {
    throw new Error('Neither requestVideoFrameCallback nor requestAnimationFrame is available');
  }

  let lastCurrentTime = null;
  let nonAdvancingPolls = 0;
  const collection = await collectScheduledSamples({
    request: requestRaf,
    cancel: cancelRaf,
    onCallback: (callbackTime) => {
      const currentTime = Number(video.currentTime);
      if (!Number.isFinite(currentTime)) {
        nonAdvancingPolls += 1;
        return null;
      }
      if (lastCurrentTime !== null && currentTime <= lastCurrentTime) {
        nonAdvancingPolls += 1;
        return null;
      }
      lastCurrentTime = currentTime;
      return {
        callbackTimeMs: isFiniteNumber(callbackTime)
          ? Number(callbackTime)
          : dependencies.now(),
        currentTimeMs: currentTime * 1000,
      };
    },
    durationMs,
    maxFrames,
    ...dependencies,
    signal: options.signal,
  });

  const summary = summarizeTimestamps(
    collection.samples.map((sample) => sample.currentTimeMs),
    {
      expectedFps: configuredFps,
      inferLosses: true,
      stabilityThresholds: options.stabilityThresholds,
    },
  );

  return {
    source: 'requestAnimationFrame/currentTime',
    timeline: 'currentTime',
    declared,
    ...summary,
    callbacks: {
      total: collection.callbackCount,
      accepted: collection.samples.length,
      nonAdvancing: nonAdvancingPolls,
    },
    stoppedBy: collection.stoppedBy,
  };
}

/** Mesure la cadence de rafraîchissement effectivement livrée par rAF. */
export async function measureDisplayRefreshRate(options = {}) {
  const { durationMs, maxFrames } = normalizeCollectionOptions(options);
  const dependencies = schedulingDependencies(options);
  if (!dependencies.now) throw new Error('A monotonic clock is unavailable');

  const nativeRaf = typeof globalThis.requestAnimationFrame === 'function'
    ? globalThis.requestAnimationFrame.bind(globalThis)
    : null;
  const nativeCancelRaf = typeof globalThis.cancelAnimationFrame === 'function'
    ? globalThis.cancelAnimationFrame.bind(globalThis)
    : null;
  const requestRaf = optionalFunction(options, 'requestAnimationFrame', nativeRaf);
  const cancelRaf = optionalFunction(options, 'cancelAnimationFrame', nativeCancelRaf);
  if (!requestRaf) throw new Error('requestAnimationFrame is unavailable');

  const collection = await collectScheduledSamples({
    request: requestRaf,
    cancel: cancelRaf,
    onCallback: (callbackTime) => (
      isFiniteNumber(callbackTime) ? Number(callbackTime) : dependencies.now()
    ),
    durationMs,
    maxFrames,
    ...dependencies,
    signal: options.signal,
  });
  const summary = summarizeTimestamps(collection.samples, {
    expectedFps: options.expectedFps,
    inferLosses: true,
    stabilityThresholds: options.stabilityThresholds,
  });

  return {
    source: 'requestAnimationFrame',
    timeline: 'callbackTime',
    ...summary,
    callbacks: {
      total: collection.callbackCount,
      accepted: collection.samples.length,
    },
    stoppedBy: collection.stoppedBy,
  };
}
